import { useState } from "react";
import { AutoComplete } from "primereact/autocomplete";
import ProdutoCatalogoService from "../../../../services/resource/produtoCatalogoService";
import CadastrarProdutoCatalogoModal from "./CadastrarProdutoCatalogoModal";

const produtoCatalogoService = new ProdutoCatalogoService();
const CRIAR_NOVO = "__criar_novo__";

/**
 * Autocomplete de produtos do catálogo — pesquisa por código (id), nome ou descrição, com uma
 * última sugestão "+ Cadastrar novo produto" quando o termo digitado não corresponde a nenhum
 * produto já cadastrado. Ao selecionar essa sugestão, abre o cadastro rápido sem perder o texto
 * já digitado nem os outros campos já preenchidos na linha (o valor final só é aplicado via
 * onChange, o componente pai é quem decide o que fazer com o restante da linha).
 */
export default function ProdutoAutoComplete({ value, onChange, placeholder = "Digite para pesquisar..." }) {
    const [termo, setTermo] = useState(value?.nome || "");
    const [sugestoes, setSugestoes] = useState([]);
    const [modalCadastroAberto, setModalCadastroAberto] = useState(false);

    function buscar(evento) {
        const query = evento.query.trim();
        if (query.length < 2) {
            setSugestoes([]);
            return;
        }

        produtoCatalogoService.autocomplete(query)
            .then(res => setSugestoes([...res.data]))
            .catch(() => setSugestoes([{ __criarNovo: true, id: CRIAR_NOVO, nome: query }]));
    }

    function selecionar(evento) {
        const produto = evento.value;
        if (produto?.__criarNovo) {
            setModalCadastroAberto(true);
            return;
        }
        setTermo(produto.nome);
        onChange(produto);
    }

    // onChange também dispara na seleção de um item (evento.target.value vira o objeto do produto,
    // não mais uma string) — nesse caso quem já cuidou de tudo foi `selecionar` (via onSelect), então
    // aqui só tratamos a digitação de texto mesmo.
    function aoMudarTexto(evento) {
        const valor = evento.target.value;
        if (typeof valor !== "string") return;

        setTermo(valor);
        if (!valor.trim()) {
            onChange(null);
        }
    }

    function aoCadastrar(produtoCriado) {
        setModalCadastroAberto(false);
        setTermo(produtoCriado.nome);
        onChange(produtoCriado);
    }

    // Se o usuário digitar algo e sair do campo sem selecionar um produto (nem da lista, nem
    // cadastrando um novo), o texto digitado é descartado e o campo volta a refletir o produto
    // realmente selecionado (ou fica vazio, se nenhum estiver selecionado).
    function aoSair() {
        const nomeSelecionado = value?.nome || "";
        if (termo !== nomeSelecionado) {
            setTermo(nomeSelecionado);
        }
    }

    return (
        <>
            <AutoComplete
                value={termo}
                suggestions={sugestoes}
                completeMethod={buscar}
                field="nome"
                onChange={aoMudarTexto}
                onSelect={selecionar}
                onBlur={aoSair}
                placeholder={placeholder}
                className="w-full"
                inputClassName="w-full h-9 px-3 text-sm border border-input rounded-lg bg-card outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                panelClassName="text-sm"
                itemTemplate={item => !item.__criarNovo &&
                        <div>
                            <div className="font-medium">{item.nome}</div>
                            {item.descricao && <div className="text-xs text-muted-foreground">{item.descricao}</div>}
                        </div>
                    }
                emptyMessage="Nenhum produto encontrado"
            />

            <CadastrarProdutoCatalogoModal
                isOpen={modalCadastroAberto}
                onClose={() => setModalCadastroAberto(false)}
                nomeInicial={termo}
                onCriado={aoCadastrar}
            />
        </>
    );
}
